import { Stack } from '@mui/material';
import React from 'react';

import { CheckIcon } from '@/shared/assets/icons/check-icon';
import {
  MainContent,
  MenuItemLabel,
  SelectablePlaceholder,
  SpinnerSlot,
  StyledMenuItem,
  TooltipAdditionalInfo,
  TrailingContent,
} from '@/shared/components/ui/menu-item/menu-item.styled';
import { TMenuItemProps } from '@/shared/components/ui/menu-item/menu-item.types';
import { Spinner } from '@/shared/components/ui/spinner/spinner';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { useHasPermission } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { useTextOverflow } from '@/shared/hooks/use-text-overflow/use-text-overflow';

export const MenuItem: React.FC<TMenuItemProps> = ({
  variant,
  icon,
  loading,
  disabled,
  selected,
  additionalInfo,
  children,
  tooltip,
  tooltipProps,
  permission,
  ...rest
}) => {
  const isSelectable = typeof selected === 'boolean';
  const hasPermission = useHasPermission(permission);

  // Only a string: element children lay themselves out as siblings in the row,
  // and one wrapper around them would stack them instead.
  const label = typeof children === 'string' ? children : undefined;
  const { ref, isOverflowing } = useTextOverflow<HTMLSpanElement>(label);
  const { ref: trailingRef, isOverflowing: isAdditionalInfoClipped } =
    useTextOverflow<HTMLDivElement>(label);

  // An item that already has something to say keeps saying it under its
  // clipped label, rather than opening a second tooltip over the first.
  const getTooltipTitle = () => {
    if (!tooltip || !isOverflowing) {
      return tooltip;
    }

    return (
      <Stack gap="0.25rem">
        <span>{label}</span>
        {tooltip}
      </Stack>
    );
  };

  // Each clipped part explains itself beside its own ellipsis.
  const labelTooltip = isOverflowing && !tooltip ? label : '';
  const additionalInfoTooltip = isAdditionalInfoClipped ? (
    <TooltipAdditionalInfo>{additionalInfo}</TooltipAdditionalInfo>
  ) : (
    ''
  );

  if (!hasPermission) {
    return null;
  }

  return (
    <Tooltip title={getTooltipTitle()} {...tooltipProps} wrapper="fullWidth">
      <StyledMenuItem
        $variant={variant}
        disabled={disabled || loading}
        {...rest}
      >
        <MainContent
          $variant={variant}
          $hasAdditionalInfo={Boolean(additionalInfo)}
        >
          {loading ? (
            <SpinnerSlot>
              <Spinner size="sm" color="inherit" />
            </SpinnerSlot>
          ) : (
            icon
          )}
          {label ? (
            <Tooltip title={labelTooltip}>
              <MenuItemLabel ref={ref}>{label}</MenuItemLabel>
            </Tooltip>
          ) : (
            children
          )}
        </MainContent>
        {(additionalInfo || isSelectable) && (
          <TrailingContent
            ref={trailingRef}
            $hasAdditionalInfo={Boolean(additionalInfo)}
          >
            {additionalInfo && (
              <Tooltip title={additionalInfoTooltip} wrapper="default">
                <>{additionalInfo}</>
              </Tooltip>
            )}
            {isSelectable &&
              (selected ? (
                <CheckIcon variant="primary" />
              ) : (
                <SelectablePlaceholder />
              ))}
          </TrailingContent>
        )}
      </StyledMenuItem>
    </Tooltip>
  );
};
